// src/services/offline/syncQueue.service.ts
import { v4 as uuidv4 } from 'uuid';
import { indexedDBService } from './indexedDB.service';
import { SyncQueueItem, SyncOperation, SyncStatus } from '../../types/offline.types';

class SyncQueueService {
  private maxRetries = 3;
  private retryDelayMs = 5000;

  /**
   * Add an operation to the sync queue
   */
  async enqueue(
    collection: string,
    documentId: string,
    operation: SyncOperation,
    data: any
  ): Promise<string> {
    const item: SyncQueueItem = {
      id: uuidv4(),
      collection,
      documentId,
      operation,
      data,
      timestamp: Date.now(),
      attempts: 0,
      status: 'pending',
    };

    await indexedDBService.addToSyncQueue(item);
    return item.id;
  }

  /**
   * Get all items pending sync
   */
  async getPending(): Promise<SyncQueueItem[]> {
    return indexedDBService.getPendingSyncItems();
  }

  /**
   * Get the full sync queue
   */
  async getAll(): Promise<SyncQueueItem[]> {
    return indexedDBService.getSyncQueue();
  }

  /**
   * Mark an item as syncing
   */
  async markAsSyncing(id: string): Promise<void> {
    await indexedDBService.updateSyncQueueItem(id, {
      status: 'syncing',
      attempts: (await this.getItem(id))?.attempts || 0 + 1,
    });
  }

  /**
   * Mark an item as synced (and remove from queue)
   */
  async markAsSynced(id: string): Promise<void> {
    await indexedDBService.removeSyncQueueItem(id);
  }

  /**
   * Mark an item as failed
   */
  async markAsFailed(id: string, error: string): Promise<void> {
    const item = await this.getItem(id);
    if (!item) return;

    const attempts = item.attempts + 1;
    const status: SyncStatus = attempts >= this.maxRetries ? 'conflict' : 'pending';

    await indexedDBService.updateSyncQueueItem(id, {
      status,
      attempts,
      error,
    });
  }

  /**
   * Mark an item as having a conflict
   */
  async markAsConflict(id: string, conflictData: any): Promise<void> {
    await indexedDBService.updateSyncQueueItem(id, {
      status: 'conflict',
      conflictData,
    });
  }

  /**
   * Get a specific queue item
   */
  async getItem(id: string): Promise<SyncQueueItem | undefined> {
    const queue = await indexedDBService.getSyncQueue();
    return queue.find((item) => item.id === id);
  }

  /**
   * Remove an item from the queue
   */
  async remove(id: string): Promise<void> {
    await indexedDBService.removeSyncQueueItem(id);
  }

  /**
   * Retry a failed item
   */
  async retry(id: string): Promise<void> {
    await indexedDBService.updateSyncQueueItem(id, {
      status: 'pending',
      error: undefined,
    });
  }

  /**
   * Clear all resolved conflicts
   */
  async clearConflicts(): Promise<void> {
    const queue = await this.getAll();
    const conflicts = queue.filter((item) => item.status === 'conflict');
    await Promise.all(conflicts.map((item) => this.remove(item.id)));
  }

  /**
   * Get count of items by status
   */
  async getStats(): Promise<Record<SyncStatus, number>> {
    const queue = await this.getAll();
    return queue.reduce(
      (acc, item) => {
        acc[item.status] = (acc[item.status] || 0) + 1;
        return acc;
      },
      { pending: 0, syncing: 0, synced: 0, conflict: 0 } as Record<SyncStatus, number>
    );
  }

  /**
   * Check if there are items pending sync
   */
  async hasPending(): Promise<boolean> {
    const pending = await this.getPending();
    return pending.length > 0;
  }

  /**
   * Get the next item to sync
   */
  async getNext(): Promise<SyncQueueItem | undefined> {
    const pending = await this.getPending();
    return pending[0];
  }

  /**
   * Clear the entire queue
   */
  async clear(): Promise<void> {
    await indexedDBService.clearSyncQueue();
  }

  /**
   * Batch enqueue multiple operations
   */
  async enqueueBatch(
    items: Array<{
      collection: string;
      documentId: string;
      operation: SyncOperation;
      data: any;
    }>
  ): Promise<string[]> {
    const ids: string[] = [];
    for (const item of items) {
      const id = await this.enqueue(
        item.collection,
        item.documentId,
        item.operation,
        item.data
      );
      ids.push(id);
    }
    return ids;
  }
}

export const syncQueueService = new SyncQueueService();
